/**
 * @module observability
 * @description Health registry, readiness probes, and OTel metrics summary
 */

import type { ModuleManifest } from '@gzl10/nexus-sdk'
import { getHealthRegistry } from './observability.service.js'
import { readyAction, metricsSummaryAction } from './observability.entity.js'
import { diagnosticsAction } from './diagnostics.action.js'

export { HealthRegistry, getHealthRegistry, resetHealthRegistry } from './observability.service.js'
export type { HealthCheck, HealthCheckResult, HealthCheckReport, HealthStatus } from './observability.service.js'
export { getOtelConfig, resetOtelConfig } from './observability.config.js'
export type { OtelConfig } from './observability.config.js'

export const observabilityModule: ModuleManifest = {
  name: 'observability',
  label: { en: 'Observability', es: 'Observabilidad' },
  icon: 'mdi:chart-line',
  description: { en: 'Health checks, readiness probes, and metrics', es: 'Health checks, probes de disponibilidad y métricas' },
  type: 'core',
  category: 'settings',
  dependencies: ['logger'],

  init: (ctx) => {
    const registry = getHealthRegistry()
    ctx.services.register('health', registry)

    // Auto-register DB health check
    registry.register({
      name: 'database',
      check: async () => {
        try {
          await ctx.db.knex.raw('SELECT 1')
          return { status: 'ok' }
        } catch (e) {
          return { status: 'error', detail: (e as Error).message }
        }
      }
    })

    // Auto-register Redis health check (if configured)
    if (process.env['REDIS_URL']) {
      registry.register({
        name: 'redis',
        check: async () => {
          try {
            const cacheManager = ctx.services.getOptional<import('@kernel/cache/cache-manager.js').CacheManager>('cacheManager')
            const redisClient = cacheManager?.redis
            if (!redisClient) return { status: 'degraded', detail: 'Redis configured but not connected (using memory fallback)' }
            await redisClient.ping()
            return { status: 'ok' }
          } catch (e) {
            return { status: 'error', detail: (e as Error).message }
          }
        }
      })
    }

    ctx.core.logger.debug('Health registry initialized with database check')
  },

  definitions: [],

  actions: [readyAction, metricsSummaryAction, diagnosticsAction],

  routePrefix: '/observability'
}
